import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import {
  resolveCardPinyinAudioTrack,
  resolveCardPinyinAudioTracks,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.audio";
import { useCallback, useMemo } from "react";
import { useProgrammaticAudio } from "#use-programmatic-audio";

type PinyinAudioCard = {
  pinyinList: readonly { toneNumbersPinyin: string }[];
} & ({ grapheme: string } | { front: string });

function pinyinAudioCardGrapheme(card: PinyinAudioCard): string {
  return "grapheme" in card ? card.grapheme : card.front;
}

/**
 * Programmatic pinyin-audio playback for a grapheme card. Intended for the
 * learn layout's effect-driven playback paths (card reveal, "View
 * Pronunciation", mnemonics tab activation while inside learn). All
 * browse-layout pinyin audio must go through a Pressable + sound icon — never
 * this hook.
 */
export function useProgrammaticGraphemePinyinAudio(
  card: PinyinAudioCard,
  options: { isMuted: boolean },
) {
  const {
    playSequence: playTrackSequence,
    playTrack,
    stop,
  } = useProgrammaticAudio({
    isMuted: options.isMuted,
  });
  const studyPinyinList = usePrevalentPinyin(pinyinAudioCardGrapheme(card), card.pinyinList);

  const playForPinyin = useCallback(
    async (pinyinKey: string) => {
      const track = resolveCardPinyinAudioTrack(studyPinyinList, pinyinKey);
      if (track === null) {
        return;
      }
      await playTrack(track);
    },
    [playTrack, studyPinyinList],
  );

  const sequenceTracks = useMemo(
    () => resolveCardPinyinAudioTracks(studyPinyinList),
    [studyPinyinList],
  );

  /** Plays every study pinyin in order, or only those `shouldPlay` admits. */
  const playSequence = useCallback(
    async (shouldPlay?: (toneNumbersPinyin: string) => boolean) => {
      await playTrackSequence(
        shouldPlay === undefined
          ? sequenceTracks
          : sequenceTracks.filter((track) => shouldPlay(track.pinyin)),
      );
    },
    [playTrackSequence, sequenceTracks],
  );

  return { playForPinyin, playSequence, stop };
}
